import { Alert, AlertIcon, Box, Button, IconButton, Stack, Text, Textarea } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { modelUtils, TRUCKING_PROVIDERS, truckingProviderOptions } from '@pelicargo/utils';
import { useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { HiOutlineTrash } from 'react-icons/hi2';

import { trpc } from '../config/trpc';
import { AirportInput } from './Autocomplete/AirportInput';
import { SimpleForm } from './SimpleForm';
import { SimpleReactSelect } from './SimpleReactSelect';

const { formatRules, governedIntegrations, parseRules } = modelUtils.truckingWhitelist;

const MONOSPACE = '"Fira Code", monospace';

/** The builder only writes allows — every row is something we're opting into quoting */
type RuleRow = {
  scac?: string;
  broker?: string;
  integration?: string;
  origin?: string;
  /** Kept as a string because that's what the select round-trips; serialised back to a number */
  fpAccountId?: string;
};

const toRows = (value: string): RuleRow[] =>
  parseRules(value).rules.map((rule) => ({
    scac: rule.scac,
    broker: rule.broker,
    integration: rule.integration,
    origin: rule.origin,
    fpAccountId: rule.fpAccountId ? String(rule.fpAccountId) : undefined,
  }));

/**
 * A row with nothing filled in carries no information, and would serialise to a bare `*` — which
 * allows every carrier and quietly defeats the whole whitelist. Treat it as unfinished instead.
 */
const isUnfinished = (row: RuleRow) => !row.scac && !row.broker && !row.integration && !row.origin && !row.fpAccountId;

const toValue = (rows: RuleRow[]) =>
  formatRules(
    (rows ?? [])
      .filter((row) => !isUnfinished(row))
      .map((row) => ({
        isDeny: false,
        scac: row.scac,
        broker: row.broker,
        integration: row.integration,
        origin: row.origin,
        fpAccountId: row.fpAccountId ? Number(row.fpAccountId) : undefined,
      })),
  );

/**
 * Denies are still honoured by the quote path and can still be written in the raw view, but the
 * builder doesn't offer them — a whitelist row is an opt-in.
 */
const isBuildable = (value: string) => {
  const { rules, invalid } = parseRules(value);

  return invalid.length === 0 && rules.every((rule) => !rule.isDeny);
};

type Props = {
  value: string;
  isDisabled?: boolean;
  onSave: (value: string) => Promise<void>;
};

/**
 * Rule builder for the trucking whitelist. A SCAC typed by hand is how a lane silently loses
 * coverage, so carriers and brokers are picked from what we've actually been quoted by. The raw
 * view stays available for bulk edits and for pasting a list in.
 *
 * `value` is the initial value only, matching the uncontrolled inputs this screen uses for every
 * other configuration key.
 */
export const TruckingWhitelistInput = ({ value, isDisabled, onSave }: Props) => {
  // Opening the builder on a value it can't represent would silently rewrite it on save. An
  // unparseable entry would be dropped; a deny would flip to an allow and *permit* what it blocked.
  const [isRawMode, setIsRawMode] = useState(() => !isBuildable(value ?? ''));
  const [raw, setRaw] = useState(value ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<{ rules: RuleRow[] }>({ defaultValues: { rules: toRows(value ?? '') } });
  const { fields, append, remove } = useFieldArray({ control: methods.control, name: 'rules' });

  const queryOptions = { refetchOnWindowFocus: false };
  const { data: truckerOptions } = trpc.getTruckerOptions.useQuery(undefined, queryOptions);
  const { data: brokerOptions } = trpc.getBrokerOptions.useQuery(undefined, queryOptions);
  const { data: fulfillmentPartners } = trpc.getAllFulfillmentPartners.useQuery(null, queryOptions);

  const fpOptions = useMemo(
    () => fulfillmentPartners?.map((account) => ({ label: account?.name, value: String(account?.id) })) ?? [],
    [fulfillmentPartners],
  );

  const invalidRawEntries = useMemo(() => parseRules(raw).invalid, [raw]);

  // Filtering is opt-in per integration, so a whitelist that names none does nothing at all.
  // That's too easy to do by accident to leave invisible.
  const rows = methods.watch('rules');
  const currentValue = isRawMode ? raw : toValue(rows);
  const governed = useMemo(() => [...governedIntegrations(parseRules(currentValue).rules)], [currentValue]);
  const unrestricted = TRUCKING_PROVIDERS.filter((provider) => !governed.includes(provider));

  // Switching to the builder rewrites the value from rows, so it has to stay shut while the value
  // holds something rows can't express
  const canBuild = useMemo(() => isBuildable(raw), [raw]);

  const showRawErrors = isRawMode && invalidRawEntries.length > 0;
  const showBuilderUnavailable = isRawMode && !canBuild && invalidRawEntries.length === 0;
  const showEmptyState = !isRawMode && fields.length === 0;
  const showNothingGoverned = governed.length === 0 && currentValue.length > 0;
  const showGoverned = governed.length > 0;
  const canSaveRaw = invalidRawEntries.length === 0;

  const save = async (next: string) => {
    setIsSaving(true);
    try {
      await onSave(next);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveRules = () => save(toValue(methods.getValues('rules')));

  const handleSaveRaw = async () => {
    await save(raw);
    methods.reset({ rules: toRows(raw) });
  };

  // Each view seeds the other from what's currently on screen, so switching never loses an edit
  const handleShowRaw = () => {
    setRaw(toValue(methods.getValues('rules')));
    setIsRawMode(true);
  };

  const handleShowBuilder = () => {
    methods.reset({ rules: toRows(raw) });
    setIsRawMode(false);
  };

  return (
    <Stack spacing="3" py="2">
      <Stack direction="row" spacing="2">
        <Button size="xs" variant={isRawMode ? 'ghost' : 'solid'} isDisabled={!canBuild} onClick={handleShowBuilder}>
          Builder
        </Button>
        <Button size="xs" variant={isRawMode ? 'solid' : 'ghost'} onClick={handleShowRaw}>
          Raw text
        </Button>
      </Stack>

      <Show if={showNothingGoverned}>
        <Alert status="warning">
          <AlertIcon />
          <Text fontSize="sm">
            No rule names an integration, so nothing is being filtered. Set the integration on at least one rule to
            start filtering it.
          </Text>
        </Alert>
      </Show>

      <Show if={showGoverned}>
        <Alert status="info">
          <AlertIcon />
          <Text fontSize="sm">
            Filtering <strong>{governed.join(', ')}</strong>.
            {unrestricted.length > 0 ? ` Not filtered: ${unrestricted.join(', ')}.` : ''}
          </Text>
        </Alert>
      </Show>

      <Show if={isRawMode}>
        <Stack spacing="2">
          <Textarea
            fontFamily={MONOSPACE}
            fontSize="sm"
            rows={8}
            value={raw}
            isDisabled={isDisabled}
            onChange={(event) => setRaw(event.target.value)}
          />
          <Show if={showRawErrors}>
            <Alert status="error">
              <AlertIcon />
              <Text fontSize="sm">Invalid rule(s): {invalidRawEntries.join(', ')}</Text>
            </Alert>
          </Show>
          <Show if={showBuilderUnavailable}>
            <Alert status="info">
              <AlertIcon />
              <Text fontSize="sm">
                This value contains deny rules (<code>!</code>), which the builder can&apos;t show. Edit it here, or
                remove them to use the builder.
              </Text>
            </Alert>
          </Show>
          <Box>
            <Button
              size="sm"
              colorScheme="brand"
              isDisabled={isDisabled || !canSaveRaw}
              isLoading={isSaving}
              onClick={handleSaveRaw}
            >
              Save
            </Button>
          </Box>
        </Stack>
      </Show>

      <Show if={!isRawMode}>
        <SimpleForm submitOnBlur={false} {...methods}>
          <Stack spacing="1">
            <Show if={showEmptyState}>
              <Text fontSize="sm" opacity="0.7">
                No rules — every carrier is quoted. Add a rule to start whitelisting.
              </Text>
            </Show>

            {fields.map((field, index) => (
              <Stack key={field.id} direction="row" align="center" spacing="2">
                <Box w="72">
                  <SimpleReactSelect
                    name={`rules.${index}.scac`}
                    options={truckerOptions ?? []}
                    placeholder="Any carrier"
                    type="creatable"
                    isSearchable
                    isClearable
                    isPortaled
                    ignoreOnBlur
                  />
                </Box>
                <Box w="48">
                  <SimpleReactSelect
                    name={`rules.${index}.broker`}
                    options={brokerOptions ?? []}
                    placeholder="Any broker"
                    type="creatable"
                    isSearchable
                    isClearable
                    isPortaled
                    ignoreOnBlur
                  />
                </Box>
                <Box w="44">
                  <SimpleReactSelect
                    name={`rules.${index}.integration`}
                    options={truckingProviderOptions}
                    placeholder="Any integration"
                    isSearchable
                    isClearable
                    isPortaled
                    ignoreOnBlur
                  />
                </Box>
                <Box w="48">
                  <AirportInput
                    name={`rules.${index}.origin`}
                    displayValue="id"
                    placeholder="Any origin"
                    isClearable
                    isPortaled
                  />
                </Box>
                <Box w="56">
                  <SimpleReactSelect
                    name={`rules.${index}.fpAccountId`}
                    options={fpOptions}
                    placeholder="Any partner"
                    isSearchable
                    isClearable
                    isPortaled
                    ignoreOnBlur
                  />
                </Box>
                <IconButton
                  aria-label="Remove rule"
                  size="sm"
                  variant="ghost"
                  icon={<HiOutlineTrash />}
                  isDisabled={isDisabled}
                  onClick={() => remove(index)}
                />
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" align="center" pt="3" spacing="3">
            <Button size="sm" variant="outline" isDisabled={isDisabled} onClick={() => append({})}>
              Add rule
            </Button>
            <Button
              size="sm"
              colorScheme="brand"
              isDisabled={isDisabled}
              isLoading={isSaving}
              onClick={handleSaveRules}
            >
              Save
            </Button>
            <Text fontSize="xs" opacity="0.6" fontFamily={MONOSPACE}>
              {currentValue || '(empty — no filtering)'}
            </Text>
          </Stack>
        </SimpleForm>
      </Show>
    </Stack>
  );
};
